import React, { useState, useEffect, useCallback, useRef } from 'react';
import { accidentApi } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const TYPES = [
  { key: 'near_miss', label: 'Near Miss', bg: '#EAF7F0', border: '#CDEBDB', text: '#15654B' },
  { key: 'minor', label: 'Minor', bg: '#FFF6E6', border: '#F5DFB0', text: '#8A5A12' },
  { key: 'major', label: 'Major', bg: '#FDECEC', border: '#F5C2C0', text: '#B42318' },
  { key: 'fatal', label: 'Fatal', bg: '#11182B', border: '#11182B', text: '#FFFFFF' },
];
const typeMeta = (k) => TYPES.find((t) => t.key === k) || TYPES[0];
const today = () => new Date().toISOString().slice(0, 10);

export default function Accident() {
  // These registers are per plant: one unit's entries must never
  // appear on, or score against, another.
  const { activeUnit } = useUnit();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(today());
  const [area, setArea] = useState('');
  const [desc, setDesc] = useState('');
  const [type, setType] = useState('');       // set by drag-drop (or click fallback)
  const [dragOver, setDragOver] = useState(false);

  // Switching plants fires a fresh read while the previous one is still in
  // flight. Whichever answers last used to win, so a slow Unit 1 reply could
  // repaint the register after the user had already moved to Unit 3.
  const wanted = useRef(activeUnit);
  const load = useCallback(async () => {
    wanted.current = activeUnit;
    try {
      const r = await accidentApi.list(activeUnit);
      if (wanted.current === activeUnit) setData(r.data);
    }
    catch (e) { if (wanted.current === activeUnit) toast.error('Failed to load accident register'); }
  }, [activeUnit]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!type) { toast.error('Drag an accident type into the box first'); return; }
    setBusy(true);
    try {
      await accidentApi.add({ date, type, area, description: desc, unit: activeUnit || '' });
      toast.success('Entry saved');
      setArea(''); setDesc(''); setType('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove ${typeMeta(r.type).label} — ${r.date}?`)) return;
    try { await accidentApi.remove(r.id); await load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const sel = type ? typeMeta(type) : null;
  return (
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-[#172554]">Accident Register (Safety)</h1>
            <p className="text-sm text-[#5A6377]">
              Month-wise safety incidents. Automatically scores the Plant Head KPI <b>Safety for All on Shop Floor</b> and the HR sheets' <b>Safety for All</b>: no accident = 4 · minor = −2 each · major = −4 each · any fatal = 0 · +1 for reporting a near miss (0–4).
            </p>
          </div>

          {/* Entry card with drag-and-drop type */}
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <h3 className="font-medium text-[#11182B] mb-3">New entry</h3>
            <div className="flex flex-wrap items-start gap-4">
              <div>
                <div className="text-xs uppercase text-[#98A1B2] mb-1">Date</div>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="border border-[#E6E9F0] rounded-lg px-2.5 py-1.5 text-sm" data-testid="acc-date" />
              </div>
              <div className="flex-1 min-w-[300px]">
                <div className="text-xs uppercase text-[#98A1B2] mb-1">Type of accident — drag a chip into the box (or click it)</div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex gap-2">
                    {TYPES.map((t) => (
                      <div key={t.key} draggable data-testid={`acc-chip-${t.key}`}
                        onDragStart={(e) => e.dataTransfer.setData('text/accident-type', t.key)}
                        onClick={() => setType(t.key)}
                        style={{ background: t.bg, border: `1.5px solid ${t.border}`, color: t.text }}
                        className="px-3 py-1.5 rounded-full text-sm font-semibold cursor-grab select-none active:cursor-grabbing">
                        {t.label}
                      </div>
                    ))}
                  </div>
                  <div data-testid="acc-drop"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false);
                      const k = e.dataTransfer.getData('text/accident-type');
                      if (k) setType(k); }}
                    style={sel ? { background: sel.bg, borderColor: sel.border } : {}}
                    className={`min-w-[170px] min-h-[42px] px-4 py-2 rounded-xl border-2 border-dashed flex items-center justify-center text-sm
                      ${dragOver ? 'border-[#2563EB] bg-[#EEF3FF]' : sel ? '' : 'border-[#C9CFDC] text-[#98A1B2]'}`}>
                    {sel
                      ? <span style={{ color: sel.text }} className="font-bold">{sel.label} ✓
                          <button onClick={() => setType('')} className="ml-2 font-normal text-xs underline">change</button></span>
                      : 'Drop type here'}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-[#98A1B2] mb-1">Area of incident</div>
                <Input value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Press shop line 2" className="w-52 text-sm" data-testid="acc-area" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="text-xs uppercase text-[#98A1B2] mb-1">Details (optional)</div>
                <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What happened" className="text-sm" />
              </div>
              <div className="pt-5">
                <Button size="sm" onClick={add} disabled={busy} data-testid="acc-add">Add</Button>
              </div>
            </div>
          </div>

          {/* Month summary strip */}
          {data && data.months.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.months.slice(0, 6).map((m) => (
                <div key={m.ym} className="bg-white border border-[#E6E9F0] rounded-lg px-3 py-2 text-sm">
                  <b className="text-[#172554]">{m.month} {m.ym.slice(0, 4)}</b>
                  <span className="text-[#5A6377]"> · {m.near_miss} near miss · {m.minor} minor · {m.major} major{m.fatal ? ` · ${m.fatal} fatal` : ''}</span>
                </div>
              ))}
            </div>
          )}

          {/* Register table — Month | Date | Type | Area */}
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <h3 className="font-medium text-[#11182B] mb-3">Register</h3>
            {!data ? <p className="text-[#98A1B2]">Loading…</p> : data.rows.length === 0 ? (
              <p className="text-[#98A1B2]">No incidents recorded yet. When the register is unused, the Safety KPI stays unscored — a clean month scores 4 only once reporting has started.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                    <tr><th className="py-2 px-2 text-left">Month</th><th className="py-2 px-2 text-left">Date</th><th className="py-2 px-2 text-left">Type of Accident</th><th className="py-2 px-2 text-left">Area of Incident</th><th className="py-2 px-2 text-left">Details</th><th className="py-2 px-2 text-left">By</th><th className="py-2 px-2 w-8"></th></tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => {
                      const t = typeMeta(r.type);
                      return (
                        <tr key={r.id} className="border-b border-[#F2F4F8]">
                          <td className="py-1.5 px-2 font-medium text-[#11182B]">{r.month} {r.ym.slice(0, 4)}</td>
                          <td className="py-1.5 px-2">{r.date}</td>
                          <td className="py-1.5 px-2">
                            <span style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.text }}
                              className="px-2 py-0.5 rounded-full text-xs font-semibold">{t.label}</span>
                          </td>
                          <td className="py-1.5 px-2 text-[#5A6377]">{r.area || '—'}</td>
                          <td className="py-1.5 px-2 text-xs text-[#5A6377]">{r.description || ''}</td>
                          <td className="py-1.5 px-2 text-xs text-[#98A1B2]">{r.created_by}</td>
                          <td className="py-1.5 px-2 text-right"><button onClick={() => remove(r)} className="text-[#C2151F] text-xs">✕</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
